import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { useRealtimeEvent } from '../../../Context/RealtimeContext';
import { fetchScheduleBoard } from '../../../Shared/Api';

const mondayOf = (day) => {
	const d = dayjs(day).startOf('day');
	const weekday = d.day(); // 0=Sun
	const diff = weekday === 0 ? -6 : 1 - weekday;
	return d.add(diff, 'day');
};

/**
 * @param {{ hasScheduling: boolean, preset: string, anchorDate: import('dayjs').Dayjs }}
 */
const useScheduleBoard = ({ hasScheduling, preset, anchorDate }) => {
	const { enqueueSnackbar } = useSnackbar();
	const [board, setBoard] = useState(null);
	const [loading, setLoading] = useState(true);

	const range = useMemo(() => {
		if (preset === 'today') {
			const day = dayjs(anchorDate).startOf('day');
			return {
				from: day.format('YYYY-MM-DD'),
				to: day.format('YYYY-MM-DD'),
			};
		}
		const start = mondayOf(anchorDate);
		return {
			from: start.format('YYYY-MM-DD'),
			to: start.add(6, 'day').format('YYYY-MM-DD'),
		};
	}, [preset, anchorDate]);

	const loadBoard = useCallback(async () => {
		if (!hasScheduling) {
			setBoard(null);
			setLoading(false);
			return;
		}
		setLoading(true);
		try {
			const data = await fetchScheduleBoard({
				preset,
				from: range.from,
				to: range.to,
			});
			setBoard(data);
		} catch (error) {
			enqueueSnackbar(error.message || 'Failed to load schedule.', {
				variant: 'error',
			});
		} finally {
			setLoading(false);
		}
	}, [enqueueSnackbar, hasScheduling, preset, range.from, range.to]);

	useEffect(() => {
		loadBoard();
	}, [loadBoard]);

	const handleRealtime = useCallback(
		(message) => {
			if (message?.type === 'job.assignment_updated') {
				loadBoard();
			}
		},
		[loadBoard],
	);
	useRealtimeEvent(handleRealtime);

	return { board, loading, range, reload: loadBoard };
};

export { mondayOf };
export default useScheduleBoard;
